import api from 'api'
import {
  CACHE_TAG_ACCOUNT_LIFECYCLE_REASON_LIST,
  CACHE_TAG_ACCOUNT_LIFECYCLE_COMPETITOR_LIST,
  CACHE_TAG_ACCOUNT_LIFECYCLE_SETTINGS,
} from 'api/cacheTagTypes'

import type {
  AccountLifecycleCompetitorApi,
  AccountLifecycleReasonApi,
  AccountLifecycleReasonType,
  AccountLifecycleSettingsPatchApi,
  AccountLifecycleSettingsApi,
} from 'types/api/account-lifecycle/accountLifecycle'

const BASE = '/api/v1/core/account-lifecycle'

type ReasonListArgs = { type: AccountLifecycleReasonType; includeInactive?: boolean }
type ReasonListResponse = { reasons: AccountLifecycleReasonApi[] }
type CompetitorListResponse = { competitors: AccountLifecycleCompetitorApi[] }
type CachedQueries = { apiReducer?: { queries?: Record<string, { endpointName?: string; originalArgs?: unknown } | undefined> } }

// Each list is cached per args, so a patch must reach every combination, not a guessed one.
const cachedArgsFor = (state: unknown, endpointName: string): unknown[] =>
  Object.values((state as CachedQueries).apiReducer?.queries ?? {})
    .filter(entry => entry?.endpointName === endpointName)
    .map(entry => entry?.originalArgs)

// The row moves now and the invalidation reconciles it; otherwise the control lags a whole refetch.
const patchCache = <TDraft>(
  dispatch: (action: unknown) => { undo: () => void },
  state: unknown,
  endpointName: string,
  argsFilter: (args: unknown) => boolean,
  recipe: (draft: TDraft) => void
) =>
  cachedArgsFor(state, endpointName)
    .filter(argsFilter)
    .map(args => dispatch(api.util.updateQueryData(endpointName as never, args as never, recipe as never)))

const extendedApi = api.injectEndpoints({
  endpoints: build => ({
    getAccountLifecycleReasons: build.query<ReasonListResponse, ReasonListArgs>({
      providesTags: (result, error, { type }) => [{ type: CACHE_TAG_ACCOUNT_LIFECYCLE_REASON_LIST, id: type }],
      query: ({ type, includeInactive = false }) => ({
        url: `${BASE}/reasons`,
        method: 'GET',
        params: { type, includeInactive },
      }),
    }),

    createAccountLifecycleReason: build.mutation<
      AccountLifecycleReasonApi,
      { type: AccountLifecycleReasonType; name: string; requiresCompetitor: boolean }
    >({
      invalidatesTags: (result, error, { type }) => [{ type: CACHE_TAG_ACCOUNT_LIFECYCLE_REASON_LIST, id: type }],
      query: body => ({ url: `${BASE}/reasons`, method: 'POST', body }),
    }),

    // `type` is fixed at creation, so it is not in the body — it only tags the list this row belongs to.
    updateAccountLifecycleReason: build.mutation<
      AccountLifecycleReasonApi,
      {
        reasonId: string
        type: AccountLifecycleReasonType
        name?: string
        requiresCompetitor?: boolean
        active?: boolean
      }
    >({
      invalidatesTags: (result, error, { type }) => [{ type: CACHE_TAG_ACCOUNT_LIFECYCLE_REASON_LIST, id: type }],
      query: ({ reasonId, type: _type, ...body }) => ({ url: `${BASE}/reasons/${reasonId}`, method: 'PUT', body }),
      onQueryStarted: async ({ reasonId, type, ...changes }, { dispatch, getState, queryFulfilled }) => {
        const patches = patchCache<ReasonListResponse>(
          dispatch,
          getState(),
          'getAccountLifecycleReasons',
          args => (args as ReasonListArgs)?.type === type,
          draft => {
            const target = draft.reasons.find(reason => reason.id === reasonId)
            if (target) Object.assign(target, changes)
          }
        )
        try {
          await queryFulfilled
        } catch {
          patches.forEach(patch => patch.undo())
        }
      },
    }),

    deleteAccountLifecycleReason: build.mutation<void, { reasonId: string; type: AccountLifecycleReasonType }>({
      invalidatesTags: (result, error, { type }) => [{ type: CACHE_TAG_ACCOUNT_LIFECYCLE_REASON_LIST, id: type }],
      query: ({ reasonId }) => ({ url: `${BASE}/reasons/${reasonId}`, method: 'DELETE' }),
      onQueryStarted: async ({ reasonId, type }, { dispatch, getState, queryFulfilled }) => {
        const patches = patchCache<ReasonListResponse>(
          dispatch,
          getState(),
          'getAccountLifecycleReasons',
          args => (args as ReasonListArgs)?.type === type,
          draft => {
            const index = draft.reasons.findIndex(reason => reason.id === reasonId)
            if (index >= 0) draft.reasons.splice(index, 1)
          }
        )
        try {
          await queryFulfilled
        } catch {
          patches.forEach(patch => patch.undo())
        }
      },
    }),

    getAccountLifecycleCompetitors: build.query<CompetitorListResponse, { includeInactive?: boolean } | void>({
      providesTags: [CACHE_TAG_ACCOUNT_LIFECYCLE_COMPETITOR_LIST],
      query: args => ({
        url: `${BASE}/competitors`,
        method: 'GET',
        params: { includeInactive: args?.includeInactive ?? false },
      }),
    }),

    createAccountLifecycleCompetitor: build.mutation<AccountLifecycleCompetitorApi, { name: string }>({
      invalidatesTags: [CACHE_TAG_ACCOUNT_LIFECYCLE_COMPETITOR_LIST],
      query: body => ({ url: `${BASE}/competitors`, method: 'POST', body }),
    }),

    updateAccountLifecycleCompetitor: build.mutation<
      AccountLifecycleCompetitorApi,
      { competitorId: string; name?: string; active?: boolean }
    >({
      invalidatesTags: [CACHE_TAG_ACCOUNT_LIFECYCLE_COMPETITOR_LIST],
      query: ({ competitorId, ...body }) => ({
        url: `${BASE}/competitors/${competitorId}`,
        method: 'PUT',
        body,
      }),
      onQueryStarted: async ({ competitorId, ...changes }, { dispatch, getState, queryFulfilled }) => {
        const patches = patchCache<CompetitorListResponse>(
          dispatch,
          getState(),
          'getAccountLifecycleCompetitors',
          () => true,
          draft => {
            const target = draft.competitors.find(competitor => competitor.id === competitorId)
            if (target) Object.assign(target, changes)
          }
        )
        try {
          await queryFulfilled
        } catch {
          patches.forEach(patch => patch.undo())
        }
      },
    }),

    deleteAccountLifecycleCompetitor: build.mutation<void, { competitorId: string }>({
      invalidatesTags: [CACHE_TAG_ACCOUNT_LIFECYCLE_COMPETITOR_LIST],
      query: ({ competitorId }) => ({ url: `${BASE}/competitors/${competitorId}`, method: 'DELETE' }),
      onQueryStarted: async ({ competitorId }, { dispatch, getState, queryFulfilled }) => {
        const patches = patchCache<CompetitorListResponse>(
          dispatch,
          getState(),
          'getAccountLifecycleCompetitors',
          () => true,
          draft => {
            const index = draft.competitors.findIndex(competitor => competitor.id === competitorId)
            if (index >= 0) draft.competitors.splice(index, 1)
          }
        )
        try {
          await queryFulfilled
        } catch {
          patches.forEach(patch => patch.undo())
        }
      },
    }),

    getAccountLifecycleSettings: build.query<AccountLifecycleSettingsApi, void>({
      providesTags: [CACHE_TAG_ACCOUNT_LIFECYCLE_SETTINGS],
      query: () => ({ url: `${BASE}/settings`, method: 'GET' }),
    }),

    updateAccountLifecycleSettings: build.mutation<AccountLifecycleSettingsApi, AccountLifecycleSettingsPatchApi>({
      invalidatesTags: [CACHE_TAG_ACCOUNT_LIFECYCLE_SETTINGS],
      query: body => ({ url: `${BASE}/settings`, method: 'PATCH', body }),
      onQueryStarted: async (body, { dispatch, getState, queryFulfilled }) => {
        const patches = patchCache<AccountLifecycleSettingsApi>(
          dispatch,
          getState(),
          'getAccountLifecycleSettings',
          () => true,
          draft => {
            Object.assign(draft, body)
          }
        )
        try {
          await queryFulfilled
        } catch {
          patches.forEach(patch => patch.undo())
        }
      },
    }),
  }),
})

export const {
  useGetAccountLifecycleReasonsQuery,
  useCreateAccountLifecycleReasonMutation,
  useUpdateAccountLifecycleReasonMutation,
  useDeleteAccountLifecycleReasonMutation,
  useGetAccountLifecycleCompetitorsQuery,
  useCreateAccountLifecycleCompetitorMutation,
  useUpdateAccountLifecycleCompetitorMutation,
  useDeleteAccountLifecycleCompetitorMutation,
  useGetAccountLifecycleSettingsQuery,
  useUpdateAccountLifecycleSettingsMutation,
} = extendedApi
